import Papa from 'papaparse';

// Brand series palette, matched to the Processed Models percentile charts:
// black for the prominent (current-year) line, then the brand green ramp
// (see PERCENTILE_COLORS / generatePercentileColors in src/utils/colorUtils.ts).
// Mostly greens + blacks — not the loud generic chart palette.
const GRAPH_SERIES_COLORS = [
  '#000000', // black — prominent / current-year line
  '#879643', // brand dark green
  '#5e692e', // brand darkest olive green
  '#abb57b', // brand medium green
  '#cfd5b3', // brand light green
  '#333435', // dark grey — overflow
];

// ---------------------------------------------------------------------------
// Chart-spec contract (erode-mcp docs/frontend-graph-rendering.md).
// The `create_chart` tool writes this JSON to GCS and the model emits the path
// in a <graph> tag, exactly like <outimg> for images. The spec carries NO data;
// it references a CSV via data.csv_uri. version/type/renderer are the
// forward-compat extension points — unknown values fall back gracefully.
// ---------------------------------------------------------------------------

export interface GraphAxis {
  id: string;
  position: 'left' | 'right';
  label: string;
  type: 'linear' | 'logarithmic';
  min: number | null;
  max: number | null;
  stacked?: boolean;
}

export interface GraphSeries {
  label: string;
  type: 'line' | 'scatter' | 'bar';
  axis: string;
  column: string;
  color?: string | null;
  fill?: string | null;
  stepped?: boolean;
  // Set by validateGraphSpec when it coerces a combined "scatter+line" /
  // "line+scatter" type to 'line': draw visible point markers so the scatter
  // half of the intent survives.
  points?: boolean;
  // Optional CSV column whose per-row value is drawn as a point label (blank
  // cells render nothing). Used for the "label only selected events" flood plot.
  labels?: string | null;
}

export interface GraphSpec {
  type: string;
  version: number;
  renderer: string;
  title?: string;
 // Data can be inline (for shared snapshots, whose GCS files have expired) or remote (for regular charts, where the CSV is still live on GCS). 
 // The chart config builder handles both transparently.
  data: { csv_uri: string; inline_csv?: string };
  x: {
    column: string;
    label?: string;
    type: 'time' | 'category' | 'linear' | 'logarithmic';
    min?: number | null;
    max?: number | null;
  };
  axes: GraphAxis[];
  series: GraphSeries[];
}

export type CsvRow = Record<string, string>;

// [\s\S] not `.` — a reflowed tag with the path on its own line would otherwise leak as raw text.
const GRAPH_TAG_REGEX = /<graph>([\s\S]*?)<\/graph>/gi;

/**
 * Detect <graph>output/chart_*.json</graph> tags and strip them from the message.
 *
 * IMPORTANT: extract with a single replace()-with-callback pass (mirrors
 * extractImagesFromMessage). Do NOT drive a stateful global regex with exec()
 * while mutating the string inside the loop — after a match is removed the
 * regex's lastIndex points past later tags and silently drops the 2nd+ graph.
 */
export function extractGraphsFromMessage(
  message: string
): { paths: string[]; originalMessage: string } | null {
  const paths: string[] = [];

  const originalMessage = message.replace(GRAPH_TAG_REGEX, (_full, inner) => {
    const path = (inner as string).trim();
    if (path) paths.push(path);
    return '';
  });

  if (paths.length === 0) return null;

  return { paths, originalMessage: originalMessage.trim() };
}

/**
 * Validate a parsed spec object. Returns the typed spec or null. Unknown
 * type/version/renderer → null (caller renders a graceful fallback); this is
 * the forward-compat gate for future v2 spec shapes.
 */
export function validateGraphSpec(obj: unknown): GraphSpec | null {
  if (!obj || typeof obj !== 'object') return null;
  const s = obj as Record<string, unknown>;

  if (s.type !== 'graph' || s.version !== 1 || s.renderer !== 'chartjs') return null;

  const data = s.data as Record<string, unknown> | undefined;
  if (!data || typeof data.csv_uri !== 'string') return null;

  const x = s.x as Record<string, unknown> | undefined;
  if (!x || typeof x.column !== 'string' || typeof x.type !== 'string') return null;

  if (!Array.isArray(s.axes) || !Array.isArray(s.series)) return null;

  // Validate element shapes too, not just array-ness: a series missing `column`
  // would otherwise read row[undefined] (an all-null dataset) and a dangling
  // `axis`/axis `id` produces a Chart.js scale mismatch — both degrade silently
  // instead of hitting the clean "Chart unavailable" fallback. Require at least
  // one usable series so an empty/garbage spec fails the gate here.
  const axisIds = new Set<string>();
  for (const a of s.axes) {
    if (!a || typeof a !== 'object') return null;
    const ax = a as Record<string, unknown>;
    if (typeof ax.id !== 'string' || typeof ax.position !== 'string' || typeof ax.type !== 'string') {
      return null;
    }
    axisIds.add(ax.id);
  }

  if (s.series.length === 0) return null;
  for (const ser of s.series) {
    if (!ser || typeof ser !== 'object') return null;
    const se = ser as Record<string, unknown>;
    // `label` feeds the legend popover directly (GraphChart series list); an
    // absent label would render "undefined" there, so gate on it here.
    if (typeof se.label !== 'string') return null;
    if (typeof se.column !== 'string' || typeof se.type !== 'string') return null;
    // Chart.js resolves series `type` against its controller registry at
    // construction; an unrecognized value throws inside GraphChart's render
    // effect and takes down the whole conversation view (prod: the model emitted
    // "scatter+line" despite create_chart's line|scatter|bar contract, and the
    // spec is persisted). Coerce that known alias to a line with visible points;
    // any other unknown type fails validation → "Chart unavailable" fallback
    // (also the right degrade path for planned v2 types like errorbar/boxplot).
    if (se.type === 'scatter+line' || se.type === 'line+scatter') {
      se.type = 'line';
      se.points = true;
    } else if (se.type !== 'line' && se.type !== 'scatter' && se.type !== 'bar') {
      return null;
    }
    // Series must target a declared axis, else Chart.js silently drops it.
    if (typeof se.axis !== 'string' || !axisIds.has(se.axis)) return null;
    // `labels` is optional (absent/null is the common case); if present it must
    // name a CSV column (string).
    if (se.labels != null && typeof se.labels !== 'string') return null;
  }

  return obj as GraphSpec;
}

// --- CSV cache --------------------------------------------------------------
// Key by the full signed URL, which already embeds {conversationId}, so the
// cache is conversation-namespaced. The same CSV may back multiple charts.

export interface CsvData {
  rows: CsvRow[];
  // Raw CSV text exactly as fetched, kept so the chart can offer a verbatim
  // "download CSV" matching the inline <csv> charts.
  text: string;
}

const csvCache = new Map<string, Promise<CsvData>>();
// Bound the cache so a long charting-heavy session doesn't retain every CSV's
// text for the lifetime of the page. Map preserves insertion order, so evicting
// the first key drops the oldest entry (simple FIFO — good enough; the same CSV
// re-fetches cheaply if it's needed again later).
const CSV_CACHE_MAX = 50;

// Parse raw CSV text into rows, preserving the verbatim text for download.
// Shared by the network path (fetchCsv) and the inline path (spec.data.inline_csv).
export function parseCsvText(text: string): CsvData {
  const parsed = Papa.parse<CsvRow>(text, { header: true, skipEmptyLines: true });
  return { rows: parsed.data, text };
}

export function fetchCsv(fullUrl: string): Promise<CsvData> {
  const cached = csvCache.get(fullUrl);
  if (cached) return cached;

  const promise = (async () => {
    const res = await fetch(fullUrl);
    if (!res.ok) throw new Error(`CSV fetch failed: ${res.status}`);
    return parseCsvText(await res.text());
  })();

  // On failure, drop the key so a later render can retry.
  promise.catch(() => csvCache.delete(fullUrl));
  csvCache.set(fullUrl, promise);
  if (csvCache.size > CSV_CACHE_MAX) {
    const oldest = csvCache.keys().next().value;
    if (oldest !== undefined) csvCache.delete(oldest);
  }
  return promise;
}

// --- spec → Chart.js config -------------------------------------------------

// Parse a CSV cell to a numeric y, returning null for blanks / non-finite so
// missing values render as GAPS (spanGaps:false) rather than 0.
function parseY(raw: unknown): number | null {
  if (raw === '' || raw == null) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

// A "day of year" x-axis is a linear 0–365 index (so multiple years overlay on
// one axis for comparison) — there's no real date. Format the numeric index as
// a month/day label for readability, using a reference non-leap year.
//
// Detected from the data shape rather than the label text (which the backend
// names inconsistently — "Day of Year", "t", etc.): a linear axis whose values
// are whole numbers spanning roughly a full year (0..~365). Real time/category
// axes and short numeric ranges are untouched.
function isDayOfYearAxis(x: GraphSpec['x'], xValues: number[]): boolean {
  if (x.type !== 'linear') return false;
  if (/day\s*of\s*year/i.test(x.label ?? '')) return true;
  if (xValues.length === 0) return false;
  const min = Math.min(...xValues);
  const max = Math.max(...xValues);
  return xValues.every((v) => Number.isInteger(v)) && min >= 0 && max <= 366 && max >= 300;
}

// Years group as "1,990" under Chart.js's default formatter, and 1990-the-year is identical by value to 1990-the-elevation — so detect by name, cheaply enough to gate the value scan behind.
function isYearName(name: string | undefined): boolean {
  const n = name ?? '';
  return !/day\s*of\s*year/i.test(n) && /\byears?\b/i.test(n);
}

// Range guard, so a name-matching "Return Period (years)" axis of 2/5/100 keeps the default formatter.
function isYearValues(values: number[]): boolean {
  return values.length > 0 && values.every((v) => Number.isInteger(v) && v >= 1000 && v <= 9999);
}

// Years print bare; all other numeric ticks keep Chart.js's grouped default.
function formatYear(value: unknown): string {
  const n = Number(value);
  return Number.isInteger(n) ? String(n) : String(value);
}

function formatDayOfYear(value: unknown): string {
  const day = Math.round(Number(value));
  if (!Number.isFinite(day)) return String(value);
  // day index is 0-based (t=0 → Jan 1). 2001 is a non-leap reference year.
  const date = new Date(Date.UTC(2001, 0, 1 + day));
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' });
}

// Time-axis timestamps arrive in UTC (e.g. "2026-06-28T00:00:00Z"). Chart.js +
// the date-fns adapter render ticks/tooltips in the VIEWER's local timezone by
// default, which shifts hourly UTC data off its grid and mislabels it — so we
// format ticks and tooltips explicitly, in the caller-selected `timeZone`
// (defaults to UTC; GraphChart passes the browser's IANA zone or 'UTC' per the
// user's picker selection). Tick granularity adapts to the data span so hourly,
// multi-day, and multi-month charts all read cleanly regardless of zone.
const MS_PER_DAY = 24 * 60 * 60 * 1000;

function timeTickOptions(spanMs: number, timeZone: string): Intl.DateTimeFormatOptions {
  if (spanMs <= 2 * MS_PER_DAY) {
    return { hour: '2-digit', minute: '2-digit', hour12: false, timeZone };
  }
  if (spanMs <= 366 * MS_PER_DAY) {
    return { month: 'short', day: 'numeric', timeZone };
  }
  return { month: 'short', year: 'numeric', timeZone };
}

function formatTimeTick(value: unknown, opts: Intl.DateTimeFormatOptions): string {
  const ms = Number(value);
  if (!Number.isFinite(ms)) return String(value);
  return new Date(ms).toLocaleString('en-US', opts);
}

// `timeZoneName: 'short'` appends the correct abbreviation for whatever zone
// was resolved (e.g. "UTC", "GMT-4", "PDT") instead of a hardcoded suffix.
function formatTimeTooltip(value: unknown, timeZone: string): string {
  const ms = Number(value);
  if (!Number.isFinite(ms)) return String(value);
  return new Date(ms).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone,
    timeZoneName: 'short',
  });
}

// Short zone abbreviation ("EDT", "UTC", "GMT+5:30") resolved at `at` so a
// DST-observing zone names the period actually charted, not today's.
function timeZoneShortName(timeZone: string, at: Date): string {
  try {
    return (
      new Intl.DateTimeFormat('en-US', { timeZone, timeZoneName: 'short' })
        .formatToParts(at)
        .find((p) => p.type === 'timeZoneName')?.value ?? timeZone
    );
  } catch {
    return timeZone;
  }
}

// The backend labels time axes "Time (UTC)"; once the viewer switches to their
// local zone that suffix is wrong, so strip a trailing "(UTC)" and re-append the
// zone abbreviation actually in effect — matching what the tooltip prints.
function timeAxisTitle(label: string | undefined, timeZone: string, at: Date): string | undefined {
  if (!label) return undefined;
  const base = label.replace(/\s*\(utc\)\s*$/i, '').trim();
  return `${base} (${timeZoneShortName(timeZone, at)})`;
}

// Map the spec x.type to a Chart.js scale type, downgrading 'time' to
// 'category' when the column doesn't parse as dates (adapter is installed,
// but malformed dates shouldn't blow up the chart).
function resolveXScaleType(
  specType: GraphSpec['x']['type'],
  xValuesRaw: string[]
): 'time' | 'category' | 'linear' | 'logarithmic' {
  if (specType !== 'time') return specType;
  const sample = xValuesRaw.slice(0, 20).filter((v) => v !== '' && v != null);
  if (sample.length === 0) return 'category';
  const parseable = sample.filter((v) => !Number.isNaN(Date.parse(v))).length;
  return parseable >= sample.length * 0.6 ? 'time' : 'category';
}

// Low-opacity fill from a series colour (percentile bands).
const BAND_ALPHA = 0.2;

function toBandFill(color: string): string {
  const c = color.trim();

  const hex = c.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    // Expand shorthand (#rgb / #rgba) to full pairs, then read the rgb channels.
    let h = hex[1];
    if (h.length === 3 || h.length === 4) h = h.split('').map((d) => d + d).join('');
    if (h.length === 6 || h.length === 8) {
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, ${BAND_ALPHA})`;
    }
  }

  // rgb()/rgba(): reuse the channels, force low alpha.
  const rgb = c.match(/^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)/i);
  if (rgb) return `rgba(${rgb[1]}, ${rgb[2]}, ${rgb[3]}, ${BAND_ALPHA})`;

  // Named/unknown: can't resolve channels without the DOM — neutral translucent grey.
  return `rgba(0, 0, 0, ${BAND_ALPHA})`;
}

export interface ChartConfig {
  type: 'line' | 'scatter' | 'bar';
  data: { datasets: any[]; labels?: string[] };
  options: any;
}

/**
 * Build a Chart.js config from a validated spec + parsed CSV rows. Pure /
 * DOM-free so it is unit-testable. `timeZone` (IANA name or 'UTC') controls how
 * a time-axis's ticks/tooltip/title are displayed; defaults to UTC as a safe
 * fallback for callers that don't specify one.
 */
export function buildChartConfig(spec: GraphSpec, rows: CsvRow[], timeZone: string = 'UTC'): ChartConfig {
  const xColumn = spec.x.column;
  const xValuesRaw = rows.map((r) => r[xColumn]);
  const xScaleType = resolveXScaleType(spec.x.type, xValuesRaw);
  // For a time scale Chart.js wants parseable date values; keep raw strings for
  // both time and category, numbers for linear/log.
  const numericX = xScaleType === 'linear' || xScaleType === 'logarithmic';

  const xNums = xValuesRaw
    .map((v) => parseY(v))
    .filter((v): v is number => v != null);

  // For a time axis, measure the data span (in ms) so tick labels can pick an
  // appropriate UTC granularity (hour / day / month).
  const isTime = xScaleType === 'time';
  const xTimeMs = isTime
    ? xValuesRaw.map((v) => Date.parse(v)).filter((n) => Number.isFinite(n))
    : [];
  const timeSpanMs = xTimeMs.length ? Math.max(...xTimeMs) - Math.min(...xTimeMs) : 0;
  const timeTickOpts = timeTickOptions(timeSpanMs, timeZone);
  // Midpoint of the plotted range — the reference instant for the axis title's
  // zone abbreviation (a summer chart reads "EDT", a winter one "EST").
  const timeMidpoint = xTimeMs.length
    ? new Date((Math.min(...xTimeMs) + Math.max(...xTimeMs)) / 2)
    : new Date();

  // Category/bar axes pair points positionally; numeric/time axes plot by x value.
  const isCategory = xScaleType === 'category';

  const datasets = spec.series.map((s, index) => {
    const color = s.color ?? GRAPH_SERIES_COLORS[index % GRAPH_SERIES_COLORS.length];
    const labelColName = s.labels ?? null;
    // Build full-length, row-index-aligned arrays — we deliberately do NOT drop
    // blank-y rows. On a numeric/time axis a scatter series' blank cells simply
    // render no dot, and a line series bridges them via spanGaps (below), so the
    // fitted curve stays continuous through observed-only rows. Keeping every row
    // is what makes both invariants hold: (a) fill-between bands, which the Filler
    // plugin pairs BY ARRAY INDEX across the two boundary datasets — dropping rows
    // per series would skew the band; and (b) point labels aligned by row index.
    const points = rows.map((row) => {
      const xRaw = row[xColumn];
      const x = numericX ? parseY(xRaw) : xRaw;
      return { x, y: parseY(row[s.column]) };
    });
    const labelCol = labelColName ? rows.map((row) => row[labelColName]) : [];

    const dataset: any = {
      label: s.label,
      type: s.type,
      yAxisID: s.axis,
      data: points,
      borderColor: color,
      backgroundColor: color,
      // Non-category line series connect across blank cells (continuous fitted
      // curve through observed-only rows). Category axes keep gaps (unchanged).
      spanGaps: !isCategory,
      pointRadius: s.type === 'scatter' || s.points ? 3 : 0,
      // Chart.js draws datasets with a LOWER `order` LAST (i.e. on top) — the
      // opposite of what the array position implies. Bars get a higher order so
      // any line/scatter series in the same chart always renders above them
      // (erode-ai/erode-amplify#764), regardless of where the spec's `series`
      // array places them.
      order: s.type === 'bar' ? 1 : 0,
    };
    if (s.type === 'line' && s.stepped) dataset.stepped = s.stepped;
    // Point labels: only for series with a labels column. Blank cell → no label.
    // labelCol is full-length and row-index-aligned, so ctx.dataIndex maps
    // directly. The datalabels formatter signature is (value, context) — read the
    // index off the context (2nd arg), NOT the first (which is the {x,y} value).
    if (labelColName) {
      dataset.datalabels = {
        display: (ctx: any) => !!labelCol[ctx.dataIndex],
        formatter: (_value: unknown, ctx: any) => labelCol[ctx.dataIndex],
        align: 'top',
        anchor: 'end',
        offset: 4,
        clamp: true,
      };
    }
    return dataset;
  });

  // Fill-between: resolve each series' `fill` target (by column or label) to the
  // dataset index, then set Chart.js fill:{target:idx}. Target not found → skip.
  const colIndex = new Map<string, number>();
  const labelIndex = new Map<string, number>();
  spec.series.forEach((s, i) => {
    if (!colIndex.has(s.column)) colIndex.set(s.column, i);
    if (!labelIndex.has(s.label)) labelIndex.set(s.label, i);
  });
  spec.series.forEach((s, i) => {
    if (!s.fill) return;
    const targetIdx = colIndex.has(s.fill)
      ? colIndex.get(s.fill)
      : labelIndex.get(s.fill);
    if (targetIdx == null) return; // target not resolvable — leave line un-filled
    const ds = datasets[i];
    ds.fill = { target: targetIdx };
    ds.backgroundColor = toBandFill(s.color ?? GRAPH_SERIES_COLORS[i % GRAPH_SERIES_COLORS.length]);
    ds.pointRadius = 0;
  });

  // Fixed tick count so multiple y-axes line up: each linear/log axis splits its
  // own range into the same number of evenly-spaced ticks, giving identical pixel
  // positions (and therefore aligned gridlines) regardless of differing scales.
  const Y_TICK_COUNT = 6;

  const dayOfYear = isDayOfYearAxis(spec.x, xNums);
  // Linear only — a category axis must keep Chart.js's own callback (issue #783).
  const yearX =
    xScaleType === 'linear' && isYearName(spec.x.label || spec.x.column) && isYearValues(xNums);

  // Only the axis kinds below override tick labels; a category axis keeps Chart.js's own callback.
  const xTicks = xScaleType === 'logarithmic'
    ? { callback: (value: unknown) => Number(value).toLocaleString() }
    : isTime
    ? {
        // Label time ticks in the selected zone (positions still come from the
        // time scale); keep them horizontal and let autoSkip thin them out.
        callback: (value: unknown) => formatTimeTick(value, timeTickOpts),
        maxRotation: 0,
        autoSkipPadding: 16,
      }
    : dayOfYear
    ? { callback: (value: unknown) => formatDayOfYear(value) }
    : yearX
    ? { callback: formatYear }
    : null;

  // Scales: x + one entry per axis keyed by its id.
  const scales: Record<string, any> = {
    x: {
      type: xScaleType,
      title: spec.x.label
        ? {
            display: true,
            text: isTime ? timeAxisTitle(spec.x.label, timeZone, timeMidpoint) : spec.x.label,
          }
        : undefined,
      // Day-of-year plots span a single year: clamp to day index 0–364
      // (Jan 1 – Dec 31) so the axis ends on Dec 31 and doesn't auto-extend or
      // roll over to Jan 1 of the next year. An explicit spec min/max still wins.
      min: spec.x.min ?? (dayOfYear ? 0 : undefined),
      max: spec.x.max ?? (dayOfYear ? 364 : undefined),
      // Log axes get an explicit, readable tick set (the default log auto-ticks
      // are sparse/ugly for return periods); otherwise relabel day-of-year index
      // ticks as month/day dates. Works identically for return_period_years and
      // exceedance_prob since the filter reads the resolved scale min/max.
      afterBuildTicks:
        xScaleType === 'logarithmic'
          ? (a: any) => {
              // Covers both a return-period axis (1–1000) and a fractional
              // exceedance-probability axis (~0.001–1): the min/max filter keeps
              // only the decade-ish ticks inside the resolved range, so a sub-1
              // axis still gets ticks instead of rendering blank.
              a.ticks = [
                0.001, 0.002, 0.005, 0.01, 0.02, 0.05, 0.1, 0.2, 0.5,
                1, 1.5, 2, 3, 5, 7, 10, 20, 30, 50, 70, 100, 200, 500, 1000,
              ]
                .filter((v) => v >= a.min && v <= a.max)
                .map((v) => ({ value: v }));
            }
          : undefined,
      // Spread, never `ticks: undefined` — Chart.js mergeIf skips keys already present, dropping the scale's own tick callback (issue #783).
      ...(xTicks ? { ticks: xTicks } : {}),
    },
  };
  // Values only get collected for a name-matching axis, so an ordinary chart never copies its y data.
  const yearCandidates = new Set(
    spec.axes.filter((a) => a.type === 'linear' && isYearName(a.label)).map((a) => a.id)
  );
  const axisValues = new Map<string, number[]>();
  if (yearCandidates.size) {
    datasets.forEach((ds) => {
      if (!yearCandidates.has(ds.yAxisID)) return;
      const vals = axisValues.get(ds.yAxisID) ?? [];
      ds.data.forEach((p: { y: number | null }) => {
        if (p.y != null) vals.push(p.y);
      });
      axisValues.set(ds.yAxisID, vals);
    });
  }

  const yearAxisIds = new Set<string>();

  spec.axes.forEach((axis, axisIndex) => {
    const yearAxis = yearCandidates.has(axis.id) && isYearValues(axisValues.get(axis.id) ?? []);
    if (yearAxis) yearAxisIds.add(axis.id);
    scales[axis.id] = {
      type: axis.type,
      position: axis.position,
      title: axis.label ? { display: true, text: axis.label } : undefined,
      min: axis.min ?? undefined,
      max: axis.max ?? undefined,
      stacked: axis.stacked ?? undefined,
      // Same number of ticks on every y-axis → ticks/gridlines align.
      ticks: { count: Y_TICK_COUNT, ...(yearAxis ? { callback: formatYear } : {}) },
      // Only the first y-axis draws horizontal gridlines across the plot, so a
      // second axis doesn't double up offset gridlines on top of the first.
      grid: { drawOnChartArea: axisIndex === 0 },
    };
  });

  // Tooltip title: zoned datetime, a real date instead of the day-of-year index, or a bare year.
  const tooltipTitle = dayOfYear
    ? (items: any[]) => (items.length ? formatDayOfYear(items[0].parsed.x) : '')
    : isTime
    ? (items: any[]) => (items.length ? formatTimeTooltip(items[0].parsed.x, timeZone) : '')
    : yearX
    ? (items: any[]) => (items.length ? formatYear(items[0].parsed.x) : '')
    : null;

  // Body line for a year-valued y-axis; other series keep Chart.js's own formatting, including its drop-the-prefix rule for an empty series label.
  const tooltipLabel = yearAxisIds.size
    ? (item: any) => {
        const text = yearAxisIds.has(item.dataset.yAxisID)
          ? formatYear(item.parsed.y)
          : item.formattedValue;
        return item.dataset.label ? `${item.dataset.label}: ${text}` : text;
      }
    : null;

  const tooltipCallbacks: Record<string, unknown> = {};
  if (tooltipTitle) tooltipCallbacks.title = tooltipTitle;
  if (tooltipLabel) tooltipCallbacks.label = tooltipLabel;

  // Base chart type: prefer the first series type, defaulting to line. Per-dataset
  // `type` overrides this, enabling mixed line/scatter/bar charts.
  const baseType = (spec.series[0]?.type ?? 'line') as ChartConfig['type'];

  return {
    type: baseType,
    // Category ticks come from data.labels — without it CategoryScale reads a throwaway [] and falls back to the row index.
    data: { datasets, ...(isCategory ? { labels: xValuesRaw } : {}) },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      // 'index' → hovering anywhere shows every series' value at that x in one
      // tooltip, rather than just the nearest single point.
      interaction: { mode: 'index', intersect: false },
      plugins: {
        // Legend is rendered as a toggle popover in GraphChart (the inline
        // Chart.js legend takes too much space; labels also show on hover).
        legend: { display: false },
        // Title is rendered as the card header in GraphChart; suppress the
        // in-canvas title to avoid showing it twice.
        title: { display: false },
        // datalabels plugin is registered per-instance in GraphChart. Default it
        // off so series without a labels column draw nothing; label-bearing series
        // override this via their per-dataset `datalabels` config above.
        datalabels: { display: false },
        tooltip: {
          mode: 'index',
          intersect: false,
          // Tooltips come from getLabelForValue, NOT ticks.callback — any axis that relabels ticks must relabel here too.
          ...(Object.keys(tooltipCallbacks).length ? { callbacks: tooltipCallbacks } : {}),
        },
        // ForecastGraph globally registers a custom 'verticalLine' plugin whose
        // afterDraw reads chart.scales.y — a scale id our charts don't have
        // (we use yL/yR), which throws on hover. Disable it for these charts.
        verticalLine: false,
      },
      scales,
    },
  };
}
